import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useProductions } from './useProductions';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import ProductionDetails from './ProductionDetails';

const Productions = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const { productions, isLoading, error, mutate } = useProductions(searchQuery);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const NAV_URL = `productions`;
  const URL = `productions`;

  mutate();

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  const handleClear = () => {
    setSearchQuery("");
    mutate();
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedProduction(null);
  };

  const handleView = (productions) => {
    setSelectedProduction(productions);
  };

  const handleEdit = (production_id) => {
    navigate(`/${NAV_URL}/update/${production_id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_URL}/create/`);
  };

  const handleDelete = useCallback((clientId) => {
    confirmAlert({
      title: 'Επιβεβαιώση διαγραφής',
      message: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή τη παραγωγή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${clientId}`);
              notifier("Η παραγωγή διαγράφηκε με επιτυχία.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Αποτυχία διαγραφής παραγωγής. " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'Όχι',
        }
      ]
    });
  }, [mutate, notifier]);


  const columnsToExtract = [
    { field: 'p_descr', headerName: 'Περιγραφή' },
    { field: 'no_of_episodes', headerName: 'Αριθμός επεισοδίων' },
    { field: 'episode_duration', headerName: 'Διάρκεια επεισοδίου (λεπτά)' },
    { field: 'p_aa', headerName: 'A/A' },
  ];

  const handleExportToExcel = () => {
    exportToExcel(
      productions,
      columnsToExtract,
      `Παραγωγές`
    );
  };

  const handleExportToPdf = () => {
    exportToPdf(
      productions,
      columnsToExtract,
      `Παραγωγές`
    );
  };

  const handleExportToCsv = () => {
    exportToCsv(
      productions,
      columnsToExtract,
      `Παραγωγές`
    );
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, headerAlign: 'center', },
    { field: 'p_descr', headerName: 'Περιγραφή', flex: 1, headerAlign: 'center', },
    { field: 'no_of_episodes', headerName: 'Αριθμός επεισοδίων', flex: 1, headerAlign: 'center', align: 'center' },
    { field: 'episodes_per_recording', headerName: 'Επ./εγγραφή', flex: 1, headerAlign: 'center', align: 'center' },
    { field: 'episode_duration', headerName: 'Διάρκεια επεισοδίου (λεπτά)', flex: 1, headerAlign: 'center', align: 'center' },
    { field: 'p_aa', headerName: 'A/A', flex: 1, headerAlign: 'center', align: 'center' },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Actions",
      getActions: (params) => [
        <GridActionsCellItem color="primary" icon={<EditOutlinedIcon />} label="Edit" onClick={() => handleEdit(params.row.id)} />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Productions" module="CRM" subtitle="List of productions" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={selectedProduction ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>

              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />

              {isLoading && <LinearProgress />}

              {error && <ErrorNotification ml={2} mr={2} error={error} />}

              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={productions || []}
                  columns={columns}
                  getRowId={getRowId}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedProduction(params.row)}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {selectedProduction && (
          <Grid item xs={4}>
            <ProductionDetails production={selectedProduction} onClose={handlePanelClose} />
          </Grid>
        )}
      </Grid>
    </Box>

  );
};

export default Productions;